import { Box, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ReactComponent as DealIcon } from 'assets/handshake-black.svg';
import { ReactComponent as TaskIcon } from 'assets/task-black.svg';

import { formatDealAmount } from 'helper/short-currency';
import { SearchCategoryType } from 'model/gloabalSearch.model';
import RoutePaths from 'routes/RoutePaths';
import { GlobalSearchService } from 'service/global-search.service';
import zeplinColor from 'theme/Color';
import { Constants } from 'utilities/constants';
import {
  cardChip,
  container,
  image,
  imageContainer,
  recentOpenContent,
  textContainer,
} from './styles';

dayjs.extend(relativeTime);

const Image = ({
  imageUrl,
  category,
  name,
}: {
  imageUrl: string;
  category: SearchCategoryType;
  name: string;
}) =>
  imageUrl ? (
    <img src={imageUrl} alt='icon' style={{ ...image[category] }} />
  ) : (
    <Typography variant='responsiveBody2'>{name.charAt(0).toUpperCase()}</Typography>
  );

const content: Partial<Record<SearchCategoryType, (category: any) => ReactNode>> = {
  clients: ({ name, PersonEmail = '', activeDeals = 0, profile_image_url, category }) => (
    <>
      <Box sx={{ ...imageContainer[category as SearchCategoryType] }}>
        <Image imageUrl={profile_image_url} category={category} name={name} />
      </Box>
      <Box sx={{ ...textContainer }}>
        <Typography variant='responsiveButtonMedium'>{name}</Typography>
        <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral8 }}>
          {PersonEmail}
        </Typography>
      </Box>
      <Typography variant='responsiveButtonMedium'>Active Deals: {activeDeals}</Typography>
    </>
  ),
  deals: ({ name, stageName }) => (
    <>
      <Box sx={{ ...imageContainer.deals }}>
        <DealIcon style={{ ...image.deals }} />
      </Box>
      <Box sx={{ ...textContainer }}>
        <Typography variant='responsiveButtonMedium'>{name}</Typography>
        <Chip label={stageName} sx={{ ...cardChip, color: zeplinColor.BasicBlack }} />
      </Box>
      <Typography variant='responsiveButtonMedium'>{formatDealAmount(4000000)}</Typography>
    </>
  ),
  tasks: ({ name, priority }) => {
    const chipColor = Constants.NewPriorityTask[priority as keyof typeof Constants.NewPriorityTask];

    return (
      <>
        <Box sx={{ ...imageContainer.tasks }}>
          <TaskIcon style={{ ...image.tasks }} />
        </Box>
        <Box sx={{ ...textContainer }}>
          <Typography variant='responsiveButtonMedium'>{name}</Typography>
          <Chip
            label={priority}
            sx={{ ...cardChip, bgcolor: chipColor, color: zeplinColor.Neutral1 }}
          />
        </Box>
      </>
    );
  },
  leads: ({ name, status, profile_image_url, category }) => (
    <>
      <Box sx={{ ...imageContainer[category as SearchCategoryType] }}>
        <Image imageUrl={profile_image_url} category={category} name={name} />
      </Box>
      <Box sx={{ ...textContainer }}>
        <Typography variant='responsiveButtonMedium'>{name}</Typography>
        <Chip label={status} sx={{ ...cardChip }} />
      </Box>
    </>
  ),
  contacts: ({ name, roles, profileImageUrl, category }) => (
    <>
      <Box sx={{ ...imageContainer[category as SearchCategoryType] }}>
        <Image imageUrl={profileImageUrl} category={category} name={name} />
      </Box>
      <Box sx={{ ...textContainer }}>
        <Typography variant='responsiveButtonMedium'>{name}</Typography>
        <Chip label={roles ?? 'N/A'} sx={{ ...cardChip }} />
      </Box>
    </>
  ),
  team: ({ name, title, profileImageUrl, category }) => (
    <>
      <Box sx={{ ...imageContainer[category as SearchCategoryType] }}>
        <Image imageUrl={profileImageUrl} category={category} name={name} />
      </Box>
      <Box sx={{ ...textContainer }}>
        <Typography variant='responsiveButtonMedium'>{name}</Typography>
        <Chip label={title ?? 'N/A'} sx={{ ...cardChip }} />
      </Box>
    </>
  ),
  business: ({ name, logo, category }) => (
    <>
      <Box sx={{ ...imageContainer[category as SearchCategoryType] }}>
        <Image imageUrl={logo} category={category} name={name} />
      </Box>
      <Box sx={{ ...textContainer }}>
        <Typography variant='responsiveButtonMedium'>{name}</Typography>
      </Box>
    </>
  ),
  recent: ({ name, clickedAt, profileImageUrl, logo, profile_image_url, category }) => {
    const imageUrl = profileImageUrl ?? logo ?? profile_image_url ?? '';

    return (
      <>
        <Box sx={{ ...imageContainer[category as SearchCategoryType] }}>
          <Image imageUrl={imageUrl} category={category} name={name} />
        </Box>
        <Box sx={{ ...recentOpenContent }}>
          <Typography variant='responsiveButtonMedium'>{name}</Typography>
          <Chip label={category} sx={{ ...cardChip }} />
        </Box>
        <Typography variant='responsiveButtonMedium'>{dayjs(clickedAt).fromNow()}</Typography>
      </>
    );
  },
};

const paths: Partial<Record<SearchCategoryType, (data?: any) => string>> = {
  clients: ({ id }) => `${RoutePaths.Client}/huddle/${id}`,
  deals: ({ id }) => `${RoutePaths.Deals}/${id}`,
  tasks: () => `${RoutePaths.Tasks}`,
  leads: () => `${RoutePaths.Leads}`,
  contacts: () => `${RoutePaths.Contacts}`,
  team: ({ id }) => `${RoutePaths.Team}/${id}`,
  business: ({ id }) => `${RoutePaths.Businesses}/${id}`,
};

const Card = ({
  category,
  product,
  handleCloseSearch,
}: {
  category: SearchCategoryType;
  product: any;
  handleCloseSearch?: () => void;
}) => {
  const renderContent = content[category];
  const renderPath = paths[category];

  const handleCardClick = async () => {
    await GlobalSearchService.postRecentSearch(product);
    handleCloseSearch?.();
  };

  return (
    <Link
      to={renderPath?.(product) ?? ''}
      state={{ globalSearchItemId: product.id }}
      style={{ textDecoration: 'none' }}
      onClick={() => handleCardClick()}
    >
      <Box sx={{ ...container }}>
        {typeof renderContent === 'function' ? renderContent(product) : null}
      </Box>
    </Link>
  );
};

export default Card;
